<template>
  <DashboardLayout pageTitle="Staff">
    <div class="max-w-[1200px] mx-auto w-full">
      <div class="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 class="text-pharma-text-main text-2xl font-black tracking-tight leading-tight">Staff Management List</h2>
          <p class="text-pharma-text-muted text-sm mt-1 font-medium">{{ filteredStaff.length }} active members registered in the portal</p>
        </div>
        <button 
          @click="openInviteModal"
          class="flex items-center justify-center rounded-lg h-10 px-5 bg-primary text-white text-sm font-bold hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all active:scale-95"
        >
          <span class="material-symbols-outlined mr-2 text-[20px]">person_add</span>
          Create New Staff
        </button>
      </div>

      <div class="flex flex-col gap-4 mb-6">
        <div class="flex flex-col md:flex-row gap-4 items-center">
          <div class="flex-1 w-full">
            <div class="flex items-stretch rounded-lg h-11 border border-gray-200 bg-white focus-within:border-primary transition-all">
              <div class="text-pharma-text-muted flex items-center justify-center pl-3">
                <span class="material-symbols-outlined text-[22px]">search</span>
              </div>
              <input 
                v-model="searchQuery"
                class="w-full border-none bg-transparent focus:ring-0 px-3 text-sm text-pharma-text-main placeholder:text-pharma-text-muted font-medium" 
                placeholder="Search staff by name or email..."
              />
            </div>
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <button 
            @click="roleFilter = 'All'"
            :class="roleFilter === 'All' ? 'bg-primary text-white border-primary shadow-md shadow-primary/10' : 'bg-white border-gray-200 text-pharma-text-main hover:bg-gray-50'"
            class="px-5 h-9 rounded-lg text-sm font-bold shadow-sm transition-all border"
          >
            All Members
          </button>
          <button 
            v-for="role in ['Admin', 'Staff']" 
            :key="role"
            @click="roleFilter = role"
            :class="roleFilter === role ? 'bg-primary text-white border-primary shadow-md shadow-primary/10' : 'bg-white border-gray-200 text-pharma-text-main hover:bg-gray-50'"
            class="px-5 h-9 border text-sm font-bold rounded-lg transition-all"
          >
            {{ role }}s
          </button>
        </div>
      </div>

      <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden min-h-[400px]">
        <table class="w-full text-left border-collapse table-fixed">
          <thead>
            <tr class="bg-gray-50/50 border-b border-gray-200">
              <th class="px-6 py-4 text-sm font-bold text-pharma-text-muted w-[240px]">Name</th>
              <th class="px-6 py-4 text-sm font-bold text-pharma-text-muted w-[280px]">Email Address</th>
              <th class="px-6 py-4 text-sm font-bold text-pharma-text-muted w-[140px]">Role</th>
              <th class="px-6 py-4 text-sm font-bold text-pharma-text-muted w-[200px]">Date Created</th>
              <th class="px-6 py-4 text-sm font-bold text-pharma-text-muted text-right w-[140px]">Actions</th>
            </tr>
          </thead>
          
          <tbody class="divide-y divide-gray-100">
            <tr v-if="loading">
              <td colspan="5" class="px-6 py-20 text-center">
                <span class="material-symbols-outlined animate-spin text-primary text-4xl">progress_activity</span>
                <p class="text-sm text-pharma-text-muted mt-2 font-bold tracking-tight italic">Loading records...</p>
              </td>
            </tr>

            <tr v-else v-for="staff in filteredStaff" :key="staff.db_id" class="hover:bg-gray-50 transition-colors group">
              <td class="px-6 py-4">
                <p class="text-sm font-bold text-pharma-text-main tracking-tight">{{ staff.name }}</p>
              </td>
              <td class="px-6 py-4">
                <p class="text-sm font-medium text-pharma-text-muted">{{ staff.email }}</p>
              </td>
              <td class="px-6 py-4">
                <span :class="`inline-flex px-3 py-0.5 rounded-full text-xs font-bold ${staff.roleColor}`">
                  {{ staff.role }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm text-pharma-text-muted font-medium">{{ staff.dateCreated }}</td>
              <td class="px-6 py-4 text-right">
                <div class="flex justify-end gap-3">
                  <button @click="openEditModal(staff)" class="text-primary hover:underline font-bold text-sm transition-all">Edit</button>
                  <span class="text-gray-200 font-light">|</span>
                  <button @click="confirmDelete(staff)" class="text-red-500 hover:underline font-bold text-sm transition-all">Delete</button>
                </div>
              </td>
            </tr>

            <tr v-if="!loading && filteredStaff.length === 0">
              <td colspan="5" class="px-6 py-20 text-center text-pharma-text-muted italic font-medium">
                No matching staff records found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-300" @click="closeModal"></div>
        <div class="relative bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in duration-300">
          <div class="p-8">
            <h3 class="text-2xl font-black text-pharma-text-main text-center mb-8 tracking-tight">
              {{ isEditing ? 'Edit Profile' : 'Create New Staff' }}
            </h3>
            
            <div v-if="errorLabel" class="mb-4 p-3 bg-red-50 text-red-600 rounded-xl text-xs font-bold border border-red-100 flex items-center gap-2">
              <span class="material-symbols-outlined text-[16px]">error</span>
              {{ errorLabel }}
            </div>

            <div class="space-y-4">
              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">Full Name</label>
                <input v-model="form.name" type="text" class="w-full h-11 px-4 rounded-xl border border-gray-200 focus:border-primary outline-none text-sm bg-[#f8f9fb] font-medium" placeholder="Enter full name" />
              </div>

              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">Email Address</label>
                <input v-model="form.email" type="email" class="w-full h-11 px-4 rounded-xl border border-gray-200 focus:border-primary outline-none text-sm bg-[#f8f9fb] font-medium" placeholder="staff@hospital.com" />
              </div>

              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">
                  {{ isEditing ? 'New Password (Optional)' : 'Password' }}
                </label>
                <div class="flex items-center rounded-xl border border-gray-200 focus-within:border-primary bg-[#f8f9fb] h-11 overflow-hidden">
                  <input v-model="form.password" :type="showPass ? 'text' : 'password'" placeholder="••••••••" class="flex-1 h-full px-4 bg-transparent outline-none text-sm text-pharma-text-main font-medium" />
                  <button @click="showPass = !showPass" type="button" class="w-10 h-full flex items-center justify-center text-pharma-text-muted hover:text-pharma-text-main">
                    <span class="material-symbols-outlined text-[18px]">{{ showPass ? 'visibility' : 'visibility_off' }}</span>
                  </button>
                </div>
              </div>

              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">Confirm Password</label>
                <div class="flex items-center rounded-xl border border-gray-200 focus-within:border-primary bg-[#f8f9fb] h-11 overflow-hidden">
                  <input v-model="form.password_confirmation" :type="showConfirmPass ? 'text' : 'password'" placeholder="••••••••" class="flex-1 h-full px-4 bg-transparent outline-none text-sm text-pharma-text-main font-medium" />
                  <button @click="showConfirmPass = !showConfirmPass" type="button" class="w-10 h-full flex items-center justify-center text-pharma-text-muted hover:text-pharma-text-main">
                    <span class="material-symbols-outlined text-[18px]">{{ showConfirmPass ? 'visibility' : 'visibility_off' }}</span>
                  </button>
                </div>
              </div>

              <div class="space-y-1.5">
                <label class="text-sm font-bold text-pharma-text-main">Staff Role</label>
                <select v-model="form.role" class="w-full h-11 px-4 rounded-xl border border-gray-200 focus:border-primary outline-none text-sm bg-[#f8f9fb] font-bold text-pharma-text-main">
                  <option value="Admin">Admin</option>
                  <option value="Staff">Staff</option>
                </select>
              </div>

              <div class="pt-4 flex gap-3">
                <button @click="closeModal" type="button" class="flex-1 h-12 bg-gray-100 font-bold rounded-xl hover:bg-gray-200 transition text-pharma-text-main">Cancel</button>
                <button @click="handleSubmit" :disabled="submitting" class="flex-1 h-12 bg-primary text-white font-bold rounded-xl shadow-lg transition-all active:scale-95 hover:bg-primary/90 flex items-center justify-center gap-2">
                  <span v-if="submitting" class="material-symbols-outlined animate-spin text-[18px]">progress_activity</span>
                  {{ isEditing ? 'Update Profile' : 'Register Staff' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showDeleteModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-md" @click="showDeleteModal = false"></div>
        <div class="relative bg-white rounded-3xl w-full max-w-md shadow-2xl p-8 text-center animate-in zoom-in duration-200">
          <div class="flex items-center justify-center w-14 h-14 bg-red-50 rounded-full mb-6 mx-auto">
            <span class="material-symbols-outlined text-red-600 text-3xl">warning</span>
          </div>
          <h3 class="text-xl font-bold text-pharma-text-main mb-2 tracking-tight">Remove Staff Member?</h3>
          <p class="text-sm text-pharma-text-muted mb-8 font-medium">
            Are you sure you want to delete <span class="font-bold text-pharma-text-main">{{ staffToDelete?.name }}</span>? 
            This action cannot be undone and will revoke all portal access.
          </p>
          <div class="flex gap-4">
            <button @click="showDeleteModal = false" class="flex-1 py-3 text-sm font-bold bg-gray-100 rounded-xl text-pharma-text-main">Cancel</button>
            <button @click="handleDelete" class="flex-1 py-3 text-sm font-bold text-white bg-red-600 rounded-xl shadow-lg shadow-red-200 hover:bg-red-700 transition">Delete Member</button>
          </div>
        </div>
      </div>
    </Teleport>
  </DashboardLayout>
</template>

<script>
import DashboardLayout from './layouts/DashboardLayout.vue'
import api from '@/services/api'

export default {
  name: 'StaffManagement',
  components: { DashboardLayout },
  data() {
    return {
      loading: true,
      submitting: false,
      showModal: false,
      isEditing: false,
      showDeleteModal: false,
      staffToDelete: null,
      editingId: null,
      showPass: false,
      showConfirmPass: false,
      errorLabel: '',
      searchQuery: '',
      roleFilter: 'All',
      form: { 
        name: '', 
        email: '', 
        role: 'Staff', 
        password: '', 
        password_confirmation: '' 
      },
      staffMembers: []
    }
  },
  computed: {
    filteredStaff() {
      return this.staffMembers.filter(s => {
        const name = (s.name || '').toLowerCase();
        const email = (s.email || '').toLowerCase();
        const search = (this.searchQuery || '').toLowerCase();
        const matchesSearch = name.includes(search) || email.includes(search);
        const matchesRole = this.roleFilter === 'All' || s.role === this.roleFilter;
        return matchesSearch && matchesRole;
      });
    }
  },
  methods: {
    async fetchStaffMembers() {
      this.loading = true;
      try {
        const response = await api.get('/staff');
        this.staffMembers = response.data.map(u => ({
          db_id: u.id,
          name: u.name || 'Unnamed Member',
          email: u.email || 'No Email',
          role: u.role || 'Staff',
          dateCreated: new Date(u.created_at).toLocaleDateString('en-US', { 
            month: 'short', 
            day: '2-digit', 
            year: 'numeric' 
          }),
          roleColor: u.role === 'Admin' ? 'bg-orange-50 text-orange-700' : 'bg-blue-50 text-blue-700'
        }));
      } catch (e) {
        console.error("Fetch failed", e);
      } finally {
        this.loading = false;
      }
    },
    openInviteModal() {
      this.isEditing = false;
      this.form = { name: '', email: '', role: 'Staff', password: '', password_confirmation: '' };
      this.errorLabel = '';
      this.showModal = true;
    },
    openEditModal(staff) {
      this.isEditing = true;
      this.editingId = staff.db_id;
      this.form = { name: staff.name, email: staff.email, role: staff.role, password: '', password_confirmation: '' };
      this.errorLabel = '';
      this.showModal = true;
    },
    async handleSubmit() {
      this.errorLabel = '';
      
      if (!this.form.name || !this.form.email) {
        this.errorLabel = "Please fill in name and email.";
        return;
      }

      if (!this.isEditing && !this.form.password) {
        this.errorLabel = "Password is required.";
        return;
      }

      if (this.form.password !== this.form.password_confirmation) {
        this.errorLabel = "Passwords do not match.";
        return;
      }

      this.submitting = true;
      try {
        if (this.isEditing) {
          await api.put(`/staff/${this.editingId}`, this.form);
        } else {
          await api.post('/staff', this.form);
        }
        this.showModal = false;
        this.fetchStaffMembers();
      } catch (e) {
        this.errorLabel = e.response?.data?.message || "Something went wrong.";
      } finally {
        this.submitting = false;
      }
    },
    confirmDelete(staff) {
      this.staffToDelete = staff;
      this.showDeleteModal = true;
    },
    async handleDelete() {
      try {
        await api.delete(`/staff/${this.staffToDelete.db_id}`);
        this.showDeleteModal = false;
        this.fetchStaffMembers();
      } catch (e) {
        alert("Delete action failed.");
      }
    },
    closeModal() {
      this.showModal = false;
    }
  },
  mounted() {
    this.fetchStaffMembers();
  }
}
</script>